import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, TextField, Modal, Select, MenuItem, IconButton, Autocomplete, Chip, Link} from '@mui/material'
import {Close, Cancel} from '@mui/icons-material'
import {useForm, Controller} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {phoneRegex, emailRegex, parseError} from '../../../../utils/helpers'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {accountApi} from '../../../../services'
import {useMobile} from '../../../../hooks'

export default ({open, setOpen, data, loadData, roles}) => {
    const {setLoading, setMessageBox} = useGlobalStates()
    const [branches, setBranches] = useState([])
    const [selectedBranches, setSelectedBranches] = useState([])
    const {mobile} = useMobile()
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                branch_ids: yup.array().of(yup.string()),
                branch_ids_helper: selectedBranches?.length > 0 ? yup.string().notRequired() : yup.string().required('Branches is required'),
                primary_branch_id: yup.string(),
                first_name: yup.string().required('First Name is required'),
                last_name: yup.string().required('Last Name is required'),
                email: yup.string().matches(emailRegex, 'Invalid email address').required('Email is required'),
                phone: yup.string().test('phone-optional', 'Invalid phone number', value => {
                    if (!value || value.trim() === '') return true
                    return phoneRegex.test(value)
                }),
                role_id: yup.string(),
            }),
        ),
    })

    useEffect(() => {
        accountApi.getBranches().then(
            ({
                data: {
                    data: {branches},
                },
            }) => {
                setBranches(branches)
            },
        )
    }, [])

    useEffect(() => {
        if (!(data && roles)) {
            form.reset()
            setSelectedBranches([])
        }
    }, [open])

    useEffect(() => {
        if (data && roles) {
            Object.keys(form.getValues()).forEach(field => {
                if (field == 'branch_ids') {
                    const temp = []
                    data?.branches?.forEach(val => {
                        temp.push({...branches.find(a => a.id == val?.branch_id), is_primary: val?.is_primary})
                    })
                    setSelectedBranches(temp)
                } else if (field == 'role_id') {
                    form.setValue(field, roles.find(role => role.name == data?.roles)?.id)
                } else {
                    form.setValue(field, data?.[field])
                }
            })
        }
    }, [data, roles])

    useEffect(() => {
        form.setValue(
            'branch_ids',
            selectedBranches.map(lang => lang.id),
        )
        if (selectedBranches?.length == 1 && !selectedBranches[0].is_primary) {
            selectAsPrimary(selectedBranches[0].id)
            form.setValue('primary_branch_id', selectedBranches[0].id)
        } else {
            form.setValue('primary_branch_id', selectedBranches.find(a => a.is_primary)?.id)
        }
    }, [selectedBranches])

    const submit = async team => {
        if (data) {
            try {
                setLoading(true)
                await accountApi.updateUser(data?.id, team)
                const currentRoleId = roles.find(role => role.name == data?.roles)?.id
                if (team?.role_id !== currentRoleId) {
                    await accountApi.setUserRole(data?.id, {role_id: team?.role_id || ''})
                }
                setMessageBox({
                    status: 'success',
                    title: 'Team Member Updated',
                    description: `${team?.first_name}'s details have been successfully updated. All changes are now reflected in the system.`,
                })
                setLoading(false)
                setOpen(false)
                loadData()
            } catch (error) {
                setLoading(false)
                setOpen(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            }
        } else {
            setLoading(true)
            accountApi
                .inviteUser(team)
                .then(() => {
                    setLoading(false)
                    setOpen(false)
                    setMessageBox({
                        status: 'success',
                        title: 'New Team Member Added!',
                        description: `${team?.first_name} has been added as an ${roles.find(role => role.id == team?.role_id)?.display_name} team.`,
                    })
                    loadData()
                })
                .catch(error => {
                    setLoading(false)
                    setOpen(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })
        }
    }

    const selectAsPrimary = id => {
        setSelectedBranches(prev =>
            prev.map(b => ({
                ...b,
                is_primary: b.id === id,
            })),
        )
    }

    const deleteItem = id => {
        setSelectedBranches(prev => {
            const updated = prev.filter(b => b.id !== id)
            const wasPrimary = prev.find(b => b.id === id)?.is_primary
            if (wasPrimary && updated?.length > 0) {
                updated[0].is_primary = true
            }
            return updated
        })
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    {mobile ? (
                        <>
                            <div className="header">
                                <Typography variant="h6" color="text.primary">
                                    {data ? 'Update Team Member' : 'Add New Team Member'}
                                </Typography>
                                <IconButton onClick={() => setOpen(false)}>
                                    <Close />
                                </IconButton>
                            </div>
                            <div className="actions">
                                <Button variant="outlined" onClick={() => setOpen(false)} style={{flex: 1}}>
                                    Back
                                </Button>
                                <Button variant="contained" fullWidth onClick={form.handleSubmit(submit)} style={{flex: 1}}>
                                    {data ? 'Update Member' : 'Send Invitation'}
                                </Button>
                            </div>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <Typography variant="h6" color="text.primary" className="title">
                            {data ? 'Update Team Member' : 'Add New Team Member'}
                        </Typography>
                    )}
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Branch Placement{' '}
                        <Typography variant="inputLabel" color="error">
                            *
                        </Typography>
                    </Typography>
                    <Autocomplete
                        multiple
                        options={branches}
                        value={selectedBranches}
                        onChange={(e, newVal) => setSelectedBranches(newVal)}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        getOptionLabel={opt => opt.branch_name}
                        renderInput={params => (
                            <TextField
                                {...params}
                                placeholder="Select Branches"
                                {...form.register('branch_ids_helper')}
                                error={!!form.formState.errors['branch_ids_helper']}
                                helperText={form.formState.errors['branch_ids_helper']?.message}
                            />
                        )}
                        slotProps={{chip: {sx: {display: 'none'}}}}
                    />
                    <div className="selected-items-container">
                        {selectedBranches?.map((item, index) => (
                            <div className="selected-items" key={index}>
                                <Typography variant="chipLabel" color="text.primary">
                                    {item.branch_name}
                                </Typography>
                                <div className="selected-items-actions">
                                    {item?.is_primary ? (
                                        <Chip label="Primary" color="primary" size="small" />
                                    ) : (
                                        <Link href="#" underline="hover" color="primary" onClick={() => selectAsPrimary(item?.id)}>
                                            <Typography variant="chipLabel">Set as primary</Typography>
                                        </Link>
                                    )}
                                    <IconButton onClick={() => deleteItem(item?.id)}>
                                        <Cancel color="disabled" />
                                    </IconButton>
                                </div>
                            </div>
                        ))}
                    </div>
                    <br />
                    <div className="form">
                        <div className="form-left">
                            <Typography variant="inputLabel" color="text.secondary">
                                First Name{' '}
                                <Typography variant="inputLabel" color="error">
                                    *
                                </Typography>
                            </Typography>
                            <TextField
                                placeholder="Enter your first name"
                                fullWidth
                                {...form.register('first_name')}
                                error={!!form.formState.errors['first_name']}
                                helperText={form.formState.errors['first_name']?.message}
                            />
                        </div>
                        <div className="form-right">
                            <Typography variant="inputLabel" color="text.secondary">
                                Last Name{' '}
                                <Typography variant="inputLabel" color="error">
                                    *
                                </Typography>
                            </Typography>
                            <TextField
                                placeholder="Enter your last name"
                                fullWidth
                                {...form.register('last_name')}
                                error={!!form.formState.errors['last_name']}
                                helperText={form.formState.errors['last_name']?.message}
                            />
                        </div>
                    </div>
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Email{' '}
                        {!data && (
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        )}
                    </Typography>
                    <TextField
                        placeholder="Enter your email"
                        fullWidth
                        disabled={!!data}
                        {...form.register('email')}
                        error={!!form.formState.errors['email']}
                        helperText={form.formState.errors['email']?.message}
                    />
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Phone Number
                    </Typography>
                    <TextField placeholder="Enter your phone number" fullWidth {...form.register('phone')} />
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Role
                    </Typography>
                    <Controller
                        {...form.register('role_id')}
                        control={form.control}
                        render={({field}) => (
                            <Select
                                {...field}
                                value={field.value ?? ''}
                                onChange={e => field.onChange(e.target.value)}
                                fullWidth
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                        },
                                    },
                                    anchorOrigin: {
                                        vertical: 'top',
                                        horizontal: 'left',
                                    },
                                    transformOrigin: {
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    },
                                }}
                                displayEmpty
                                renderValue={selected => {
                                    if (selected === '') {
                                        return <span style={{color: '#aaa'}}>Select your role</span>
                                    }
                                    return roles.find(item => item.id === selected)?.display_name ?? selected
                                }}>
                                {roles?.map(item => (
                                    <MenuItem key={item?.id} value={item?.id}>
                                        {item?.display_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                    <br />
                    <br />
                    {mobile ? (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <div className="actions">
                            <Button onClick={() => setOpen(false)} variant="outlined" fullWidth>
                                Cancel
                            </Button>
                            <Button variant="contained" fullWidth onClick={form.handleSubmit(submit)}>
                                {data ? 'Update Member' : 'Send Invitation'}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
